/**
 * Read Only Certification Component
 *
 * A read-only version of the certification that:
 * - Shows all questions with correct answers revealed
 * - Allows free navigation between questions
 * - Does NOT save to database
 * - Does NOT count as an attempt
 *
 * Fetches certification questions from the backend GraphQL API.
 */
import { ArrowLeft, ArrowRight, Award, X, Check, Eye } from 'lucide-react';
import { useState } from 'react';

import { useGetCertificationQuestionsQuery } from '@/@generated/codegen/API';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { useIsAccreditationUser } from '@/hooks/useIsAccreditationUser';
import { cn } from '@/lib/utils';

import { ReadOnlyCertificationProps } from '../types';

export const ReadOnlyCertification = ({
  caseId,
  caseTitle,
  onClose,
}: ReadOnlyCertificationProps) => {
  const { t } = useTranslations();
  const isAccreditationUser = useIsAccreditationUser();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const { toast } = useToast();

  const { data, loading, error } = useGetCertificationQuestionsQuery({
    variables: {
      input: { patientCaseId: caseId },
    },
    skip: !caseId,
    onError: (err) => {
      console.error('Error loading certification:', err);
      toast({
        title: t('validation.errors.loadingCertification'),
        description: 'Unable to load certification',
        variant: 'destructive',
      });
      onClose();
    },
  });

  const questions = data?.getCertificationQuestions?.certification_questions ?? [];

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="p-8">
          <p className="font-body text-lg">{t('certification.test.loadingTest')}</p>
        </Card>
      </div>
    );
  }

  if (error || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
          {/* Header with read-only badge */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-3xl text-livelinx-purple200">
                <Award className="mr-2 inline h-8 w-8" />
                {t('certification.review.title', { caseTitle })}
              </h2>
              <span className="flex items-center gap-1 rounded-full bg-livelinx-purple200/10 px-2 py-1 text-xs font-medium text-livelinx-purple200">
                <Eye className="h-3 w-3" />
                Reviewer
              </span>
            </div>
            <Button variant="ghost" onClick={onClose} size="icon">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="mb-2 flex justify-between text-sm text-livelinx-grey200">
              <span>
                {t('certification.test.questionProgress', {
                  current: currentQuestionIndex + 1,
                  total: questions.length,
                })}
              </span>
              <span>
                {t('certification.test.progressComplete', {
                  percent: progress.toFixed(0),
                })}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question */}
          <div className="mb-8">
            <h3 className="mb-4 font-body text-xl text-livelinx-text">
              {currentQuestion?.question_text}
            </h3>

            {/* Options with correct answer highlighted */}
            <div className="space-y-3">
              {currentQuestion?.options.map((option, index) => {
                const isCorrect = index === currentQuestion.correct_answer;

                return (
                  <div
                    key={index}
                    className={cn(
                      'mb-3 flex items-center space-x-3 rounded-lg border-2 p-4 transition-all',
                      isCorrect && 'border-livelinx-green bg-livelinx-green50',
                      !isCorrect && 'border-livelinx-lightgray100 bg-white'
                    )}
                  >
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                      {isCorrect && <Check className="h-5 w-5 text-livelinx-green" />}
                    </div>
                    <Label
                      className={cn(
                        'flex-1 cursor-default font-body text-base',
                        isCorrect && 'font-semibold text-livelinx-green200'
                      )}
                    >
                      {option}
                    </Label>
                  </div>
                );
              })}
            </div>

            {/* Explanation */}
            {currentQuestion?.explanation && (
              <div className="mt-6 rounded-lg border border-livelinx-blue300 bg-livelinx-blue50 p-4">
                <p className="font-body text-sm text-livelinx-text">
                  <strong className="text-livelinx-blue300">
                    {t('certification.review.explanation')}:
                  </strong>{' '}
                  {currentQuestion.explanation}
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between border-t border-livelinx-lightgray100 pt-4">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="border-livelinx-purple200 text-livelinx-purple200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('common.buttons.previous')}
            </Button>

            {currentQuestionIndex === questions.length - 1 ? (
              <Button
                onClick={onClose}
                className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
              >
                {t('common.buttons.close')}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
              >
                {isAccreditationUser ? t('common.buttons.skip') : t('common.buttons.next')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};
